import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Select from 'react-select';
import {
  PAYABLE_COLUMN_WIDTHS,
  PAYABLE_TABLE_FIELDS,
  SUBMISSION_STATUS,
  applyPayableFieldValue,
  applySharedPayableField,
  formatCellValue,
  getFieldStatus,
  getPayableHrpId,
  isPayableRetryable,
  isPayableSubmittable,
  isReleaseDatePending,
  releaseDatePendingLabel,
} from './utils';
import { fetchAdjustmentPayableReasonCodes } from '../../../utils/apiUtils';

const SHARED_FIELDS = [
  { field: 'supplierHccID', label: 'Supplier HCC ID' },
  { field: 'reasonCode', label: 'Reason code' },
];

const btnBase = {
  border: '1px solid var(--lighterGrey)',
  borderRadius: '0.35rem',
  padding: '0.45rem 0.9rem',
  fontSize: '0.875rem',
  cursor: 'pointer',
  background: 'var(--white)',
};

const btnSecondary = { ...btnBase };

const btnPrimary = {
  ...btnBase,
  background: 'var(--lightBlue)',
  borderColor: 'var(--lightBlue)',
  color: 'var(--white)',
};

const btnDanger = {
  ...btnBase,
  color: 'var(--red)',
  borderColor: 'var(--lightRed)',
};

const disabledBtn = (style, disabled) => ({
  ...style,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const issueStyle = {
  marginTop: '0.35rem',
  color: 'var(--red)',
  fontSize: '0.75rem',
  lineHeight: 1.35,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const pendingValueStyle = {
  color: 'var(--darkGrey)',
  fontStyle: 'italic',
};

const fieldInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '0.35rem 0.45rem',
  border: '1px solid var(--lighterGrey)',
  borderRadius: '0.25rem',
  fontSize: '0.875rem',
  background: 'var(--white)',
};

const payableCellStyle = (fieldStatus, hasIssue) => {
  const style = { verticalAlign: 'top' };

  if (fieldStatus === 'missing') {
    style.backgroundColor = '#fef9e7';
  } else if (fieldStatus === 'uncertain') {
    style.backgroundColor = '#fef3e2';
  }

  if (hasIssue) {
    style.boxShadow = 'inset 0 0 0 2px rgba(183, 28, 28, 0.35)';
    if (fieldStatus === 'missing' || fieldStatus === 'uncertain') {
      style.backgroundColor = '#fdecea';
    }
  }

  return style;
};

const sharedFieldStyle = (fieldStatus, hasIssue) => ({
  minWidth: '10rem',
  padding: '0.5rem 0.75rem',
  borderRadius: '0.375rem',
  border: '1px solid var(--lighterGrey)',
  ...payableCellStyle(fieldStatus, hasIssue),
});

const submissionStatusLabel = (status) => {
  if (status === SUBMISSION_STATUS.CREATED) {
    return 'Created';
  }
  if (status === SUBMISSION_STATUS.CREATE_FAILED) {
    return 'Create failed';
  }
  if (status === SUBMISSION_STATUS.REJECTED) {
    return 'Rejected';
  }
  if (status === SUBMISSION_STATUS.NOT_SUBMITTED) {
    return 'Not submitted';
  }
  if (status === SUBMISSION_STATUS.SUBMITTING) {
    return 'Unconfirmed';
  }
  return status?.replace(/_/g, ' ') ?? '';
};

const submissionStatusStyle = (status) => {
  const base = {
    display: 'inline-block',
    padding: '0.1rem 0.4rem',
    borderRadius: '0.25rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'capitalize',
    background: 'var(--lightestGrey)',
    color: 'var(--darkGrey)',
  };
  if (status === SUBMISSION_STATUS.CREATED) {
    return { ...base, background: '#e8f5e9', color: '#2e7d32' };
  }
  if (status === SUBMISSION_STATUS.CREATE_FAILED) {
    return { ...base, background: 'var(--lightRed)', color: 'var(--red)' };
  }
  if (status === SUBMISSION_STATUS.REJECTED) {
    return { ...base, background: '#fef3e2', color: '#b45309' };
  }
  if (status === SUBMISSION_STATUS.NOT_SUBMITTED) {
    return { ...base, background: '#fef9e7', color: '#856404' };
  }
  if (status === SUBMISSION_STATUS.SUBMITTING) {
    return { ...base, background: '#fef3e2', color: '#b45309' };
  }
  return base;
};

const payableRowBackgroundColor = (payable) => {
  if (!payable?.submissionStatus) {
    return undefined;
  }
  if (payable.submissionStatus === SUBMISSION_STATUS.REJECTED) {
    return '#fef3e2';
  }
  if (payable.submissionStatus === SUBMISSION_STATUS.CREATE_FAILED) {
    return '#ffe6e6';
  }
  if (payable.submissionStatus === SUBMISSION_STATUS.CREATED) {
    return '#e6ffe6';
  }
  return undefined;
};

const errorRowStyle = {
  backgroundColor: '#fef5f5',
  padding: '0.5rem 0.75rem',
  fontSize: '0.875rem',
  color: 'var(--red)',
  fontStyle: 'italic',
  borderTop: 'none',
};

// Sticky header/total cells need opaque backgrounds and, because the table uses
// border-collapse (which detaches cell borders while scrolling), a box-shadow
// divider so scrolled rows never bleed through and borders stay put.
const stickyHeaderCellStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
  background: 'var(--lighterGrey)',
  backgroundClip: 'padding-box',
  boxShadow: 'inset 0 -1px 0 var(--lightGrey)',
};

const stickyTotalCellStyle = (top) => ({
  position: 'sticky',
  top,
  zIndex: 2,
  background: 'var(--lightestGrey)',
  backgroundClip: 'padding-box',
  boxShadow: 'inset 0 -1px 0 var(--lightGrey)',
});

const addPayableRowStyle = {
  padding: '0.5rem 0.75rem',
  textAlign: 'center',
  background: 'var(--white)',
};

const addPayableButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  border: '1px dashed var(--lightBlue)',
  borderRadius: '0.35rem',
  padding: '0.4rem 0.9rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  cursor: 'pointer',
  background: 'var(--white)',
  color: 'var(--lightBlue)',
};

const SharedFieldSummary = ({
  payable,
  editable = false,
  disabled = false,
  onSharedFieldChange,
  onSharedFieldCommit,
  showValidation = false,
}) => {
  const { data: reasonCodesData, isLoading: reasonCodesLoading } = useQuery({
    queryKey: ['adjustmentPayableReasonCodes'],
    queryFn: () => fetchAdjustmentPayableReasonCodes(),
    staleTime: 5 * 60 * 1000,
    enabled: editable,
  });

  const reasonCodeOptions = useMemo(() => {
    if (!reasonCodesData?.rows) {
      return [];
    }
    return reasonCodesData.rows
      .filter((rc) => rc.adjustmentPayableReasonKey >= 0)
      .sort((a, b) =>
        (a.adjustmentPayableReasonName || '').localeCompare(
          b.adjustmentPayableReasonName || '',
        ),
      )
      .map((rc) => ({
        value: rc.adjustmentPayableReasonCode,
        label: `${rc.adjustmentPayableReasonCode} - ${rc.adjustmentPayableReasonName}`,
      }));
  }, [reasonCodesData]);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem 1.5rem',
        marginBottom: '0.75rem',
      }}
    >
      {SHARED_FIELDS.map(({ field, label }) => {
        const value = payable?.[field];
        const fieldStatus = getFieldStatus(value, field);
        const issueSummary = payable?.issues?.[field];
        const hasIssue = Boolean(issueSummary);
        const hasValue = fieldStatus !== 'missing';
        const isReasonCodeField = field === 'reasonCode';
        const showEmptyError =
          showValidation && isReasonCodeField && !hasValue && !hasIssue;
        return (
          <div
            key={field}
            style={sharedFieldStyle(fieldStatus, hasIssue || showEmptyError)}
          >
            <span
              style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--darkGrey)',
                marginBottom: '0.2rem',
              }}
            >
              {label}
            </span>
            {editable ? (
              isReasonCodeField ? (
                <>
                  <Select
                    value={
                      reasonCodeOptions.find((opt) => opt.value === value) ||
                      null
                    }
                    onChange={(selected) => {
                      onSharedFieldChange?.(field, selected?.value || '');
                      onSharedFieldCommit?.();
                    }}
                    options={reasonCodeOptions}
                    placeholder="Search or select..."
                    isClearable
                    isSearchable
                    isDisabled={disabled}
                    isLoading={reasonCodesLoading}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 'unset',
                        border: showEmptyError
                          ? '1px solid var(--red)'
                          : '1px solid var(--lighterGrey)',
                        borderRadius: '0.25rem',
                        background: state.isDisabled
                          ? 'var(--lightestGrey)'
                          : 'var(--white)',
                        fontSize: '0.875rem',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '0.35rem 0.45rem',
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      menu: (base) => ({
                        ...base,
                        fontSize: '0.875rem',
                      }),
                    }}
                  />
                  {showEmptyError && (
                    <div style={issueStyle}>Reason code is required</div>
                  )}
                </>
              ) : (
                <input
                  type="text"
                  style={fieldInputStyle}
                  value={value ?? ''}
                  disabled={disabled}
                  aria-label={label}
                  onChange={(e) => onSharedFieldChange?.(field, e.target.value)}
                  onBlur={() => onSharedFieldCommit?.()}
                />
              )
            ) : (
              hasValue && (
                <span style={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>
                  {formatCellValue(value)}
                </span>
              )
            )}
            {hasIssue && <div style={issueStyle}>{issueSummary}</div>}
          </div>
        );
      })}
    </div>
  );
};

const VendorInvoicePayablesTable = ({
  payables = [],
  rejectedIndices = new Set(),
  onReject,
  onUndoReject,
  readOnly = false,
  retryMode = false,
  editsDisabled = false,
  onPayablesChange,
  onPayablesCommit,
  onResubmit,
  resubmittingPayableId = null,
  showValidation = false,
  isPreviewMode = false,
  onAddPayable,
}) => {
  // The header and total rows are pinned via position:sticky; the total row's
  // sticky offset is the measured header height so the two never overlap.
  const theadRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = theadRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const totalAmount = useMemo(() => {
    if (payables.length <= 1) {
      return null;
    }
    return payables.reduce((sum, payable) => {
      const amount = parseFloat(payable?.payableAmount);
      return sum + (Number.isNaN(amount) ? 0 : amount);
    }, 0);
  }, [payables]);

  const canAddPayable = typeof onAddPayable === 'function';

  if (!payables.length && !canAddPayable) {
    return (
      <p style={{ color: 'var(--darkGrey)', fontSize: '0.9rem' }}>
        No payables in the current draft.
      </p>
    );
  }

  const canEdit = !readOnly && typeof onPayablesChange === 'function';
  const showSubmissionStatus = payables.some((p) => p?.submissionStatus);
  const showRejectActions =
    !retryMode && canEdit && typeof onReject === 'function';
  const showResubmitActions = retryMode && typeof onResubmit === 'function';
  const showActions = showRejectActions || showResubmitActions;
  const hasRetryableRows = payables.some(isPayableRetryable);
  const sharedSource = retryMode
    ? (payables.find(isPayableRetryable) ?? payables[0])
    : payables[0];
  const sharedEditable = canEdit && (!retryMode || hasRetryableRows);

  const handleSharedChange = (field, value) => {
    onPayablesChange(
      applySharedPayableField(payables, field, value, {
        onlyRetryable: retryMode,
      }),
    );
  };

  const handleRowChange = (index, field, value) => {
    onPayablesChange(
      payables.map((payable, i) =>
        i === index ? applyPayableFieldValue(payable, field, value) : payable,
      ),
    );
  };

  const showTotalRow = totalAmount !== null;

  // The Payable ID column rides alongside Submission (both post-submission).
  const totalColumnCount =
    PAYABLE_TABLE_FIELDS.length +
    (showSubmissionStatus ? 2 : 0) +
    (showActions ? 1 : 0);

  return (
    <div>
      <SharedFieldSummary
        payable={sharedSource}
        editable={sharedEditable}
        disabled={editsDisabled}
        onSharedFieldChange={handleSharedChange}
        onSharedFieldCommit={onPayablesCommit}
        showValidation={showValidation}
      />
      <div
        className="table-wrapper show-scrollbar"
        style={{
          // Bound the payables list so it becomes its own scroll region:
          // header + total rows stay pinned while the rows scroll beneath.
          // `show-scrollbar` re-enables a visible scrollbar, which the global
          // theme otherwise hides app-wide (see _table.scss / main.scss).
          maxHeight: 'min(60vh, 620px)',
          height: 'auto',
          overflowX: 'auto',
          overflowY: 'auto',
          width: '100%',
        }}
      >
        <table
          style={{
            tableLayout: 'fixed',
            width: 'max-content',
            minWidth: '100%',
          }}
        >
          <colgroup>
            {showActions && (
              <col style={{ width: PAYABLE_COLUMN_WIDTHS.actions }} />
            )}
            {showSubmissionStatus && (
              <col style={{ width: PAYABLE_COLUMN_WIDTHS.submission }} />
            )}
            {showSubmissionStatus && (
              <col style={{ width: PAYABLE_COLUMN_WIDTHS.payableId }} />
            )}
            {PAYABLE_TABLE_FIELDS.map((field) => (
              <col
                key={field}
                style={{ width: PAYABLE_COLUMN_WIDTHS[field] || '10rem' }}
              />
            ))}
          </colgroup>
          <thead ref={theadRef}>
            <tr>
              {showActions && (
                <th
                  style={{
                    ...stickyHeaderCellStyle,
                    width: PAYABLE_COLUMN_WIDTHS.actions,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Actions
                </th>
              )}
              {showSubmissionStatus && (
                <th
                  style={{
                    ...stickyHeaderCellStyle,
                    width: PAYABLE_COLUMN_WIDTHS.submission,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Submission
                </th>
              )}
              {showSubmissionStatus && (
                <th
                  style={{
                    ...stickyHeaderCellStyle,
                    width: PAYABLE_COLUMN_WIDTHS.payableId,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Payable ID
                </th>
              )}
              {PAYABLE_TABLE_FIELDS.map((field) => (
                <th
                  key={field}
                  style={{
                    ...stickyHeaderCellStyle,
                    width: PAYABLE_COLUMN_WIDTHS[field] || '10rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showTotalRow && (
              <tr style={{ fontWeight: 'bold' }}>
                {showActions && (
                  <td style={stickyTotalCellStyle(headerHeight)} />
                )}
                {showSubmissionStatus && (
                  <td style={stickyTotalCellStyle(headerHeight)} />
                )}
                {showSubmissionStatus && (
                  <td style={stickyTotalCellStyle(headerHeight)} />
                )}
                {PAYABLE_TABLE_FIELDS.map((field) => (
                  <td
                    key={field}
                    style={{
                      ...stickyTotalCellStyle(headerHeight),
                      width: PAYABLE_COLUMN_WIDTHS[field] || '10rem',
                      padding: '0.5rem 0.75rem',
                    }}
                  >
                    {field === 'bankAccountName' && 'Total'}
                    {field === 'payableAmount' && totalAmount.toFixed(2)}
                  </td>
                ))}
              </tr>
            )}
            {payables.map((payable, index) => {
              const reviewRejected = rejectedIndices.has(index);
              const isRejectedVisual =
                reviewRejected ||
                (!retryMode &&
                  payable?.submissionStatus === SUBMISSION_STATUS.REJECTED);
              const rowRetryable = isPayableRetryable(payable);
              const rowEditable =
                canEdit && (retryMode ? rowRetryable : !reviewRejected);
              const rowIncomplete =
                Object.keys(payable?.issues ?? {}).length > 0;
              const resubmitDisabled =
                editsDisabled ||
                !isPayableSubmittable(payable) ||
                resubmittingPayableId === payable?.payableId ||
                Boolean(resubmittingPayableId);
              const rowBackgroundColor = payableRowBackgroundColor(payable);
              return (
                <>
                  <tr key={payable?.payableId || `payable-${index}`}>
                    {showRejectActions && (
                      <td
                        style={{
                          ...(rowBackgroundColor
                            ? {
                                backgroundColor: rowBackgroundColor,
                              }
                            : {}),
                        }}
                      >
                        {reviewRejected ? (
                          <button
                            type="button"
                            style={btnSecondary}
                            onClick={() => onUndoReject(index)}
                          >
                            Undo
                          </button>
                        ) : (
                          <button
                            type="button"
                            style={btnDanger}
                            onClick={() => onReject(index)}
                          >
                            Reject
                          </button>
                        )}
                      </td>
                    )}
                    {showResubmitActions && (
                      <td
                        style={{
                          ...(rowBackgroundColor
                            ? {
                                backgroundColor: rowBackgroundColor,
                              }
                            : {}),
                        }}
                      >
                        {rowRetryable ? (
                          <button
                            type="button"
                            style={disabledBtn(btnPrimary, resubmitDisabled)}
                            disabled={resubmitDisabled}
                            onClick={() => onResubmit(payable)}
                          >
                            {resubmittingPayableId === payable?.payableId
                              ? 'Submitting…'
                              : 'Resubmit'}
                          </button>
                        ) : null}
                      </td>
                    )}
                    {showSubmissionStatus && (
                      <td
                        style={{
                          ...(rowBackgroundColor
                            ? {
                                backgroundColor: rowBackgroundColor,
                              }
                            : {}),
                          ...(isRejectedVisual
                            ? {
                                opacity: 0.45,
                                textDecoration: 'line-through',
                              }
                            : {}),
                        }}
                      >
                        <span
                          style={submissionStatusStyle(
                            payable.submissionStatus,
                          )}
                        >
                          {submissionStatusLabel(payable.submissionStatus)}
                        </span>
                      </td>
                    )}
                    {showSubmissionStatus && (
                      <td
                        style={{
                          verticalAlign: 'top',
                          fontSize: '0.8125rem',
                          wordBreak: 'break-word',
                          ...(rowBackgroundColor
                            ? {
                                backgroundColor: rowBackgroundColor,
                              }
                            : {}),
                          ...(isRejectedVisual
                            ? {
                                opacity: 0.45,
                                textDecoration: 'line-through',
                              }
                            : {}),
                        }}
                      >
                        {getPayableHrpId(payable) || '-'}
                      </td>
                    )}
                    {PAYABLE_TABLE_FIELDS.map((field) => {
                      const value = payable?.[field];
                      const releaseDatePending = isReleaseDatePending(
                        field,
                        value,
                      );
                      // Test guidance skips the HRP lookups, so these cells say so
                      // rather than read as "not found".
                      const lookupSkipped =
                        isPreviewMode &&
                        (field === 'bankAccountName' ||
                          field === 'paymentCycleID');
                      const fieldStatus =
                        releaseDatePending || lookupSkipped
                          ? 'complete'
                          : getFieldStatus(value, field);
                      const issueSummary = payable?.issues?.[field];
                      const hasIssue = Boolean(issueSummary);
                      const hasValue = fieldStatus !== 'missing';
                      return (
                        <td
                          key={field}
                          style={{
                            width: PAYABLE_COLUMN_WIDTHS[field] || '10rem',
                            ...(rowBackgroundColor
                              ? {
                                  backgroundColor: rowBackgroundColor,
                                }
                              : rowIncomplete && !isRejectedVisual
                                ? {
                                    backgroundColor: '#fffbf0',
                                  }
                                : {}),
                            ...payableCellStyle(fieldStatus, hasIssue),
                            ...(isRejectedVisual
                              ? {
                                  opacity: 0.45,
                                  textDecoration: 'line-through',
                                }
                              : {}),
                          }}
                        >
                          {rowEditable ? (
                            <input
                              type="text"
                              style={fieldInputStyle}
                              value={value ?? ''}
                              placeholder={
                                releaseDatePending
                                  ? releaseDatePendingLabel({ retryMode })
                                  : undefined
                              }
                              disabled={editsDisabled}
                              aria-label={`${field} row ${index + 1}`}
                              onChange={(e) =>
                                handleRowChange(index, field, e.target.value)
                              }
                              onBlur={() => onPayablesCommit?.()}
                            />
                          ) : releaseDatePending ? (
                            <div style={pendingValueStyle}>
                              {releaseDatePendingLabel({ retryMode })}
                            </div>
                          ) : lookupSkipped ? (
                            <div style={{ wordBreak: 'break-word' }}>
                              <div style={pendingValueStyle}>
                                Looked up on real upload
                              </div>
                              {field === 'bankAccountName' &&
                                payable?.account && (
                                  <div
                                    style={{
                                      marginTop: '0.15rem',
                                      fontSize: '0.75rem',
                                      color: 'var(--darkGrey)',
                                    }}
                                  >
                                    Invoice account: {payable.account}
                                  </div>
                                )}
                            </div>
                          ) : (
                            hasValue && (
                              <div style={{ wordBreak: 'break-word' }}>
                                {formatCellValue(value)}
                              </div>
                            )
                          )}
                          {hasIssue && (
                            <div style={issueStyle}>{issueSummary}</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {payable.submitError && (
                    <tr
                      key={`${payable?.payableId || `payable-${index}`}-error`}
                    >
                      <td colSpan={totalColumnCount} style={errorRowStyle}>
                        {payable.submitError}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {canAddPayable && (
              <tr>
                <td colSpan={totalColumnCount} style={addPayableRowStyle}>
                  <button
                    type="button"
                    style={disabledBtn(addPayableButtonStyle, editsDisabled)}
                    disabled={editsDisabled}
                    onClick={() => onAddPayable()}
                    aria-label="Add payable"
                  >
                    <span
                      aria-hidden="true"
                      style={{ fontSize: '1.05rem', lineHeight: 1 }}
                    >
                      +
                    </span>
                    Add payable
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorInvoicePayablesTable;
